import type { Tables } from "./database";

export enum GameSize {
  Small = 1,
  Medium = 2,
  Large = 3,
}

export type GameCatalogStatus = "published" | "draft" | "unlisted" | "archived" | "unknown";
export type GamePageAppearanceSource = "curated_rows" | "all_games_grid" | "tag_filtered_grid";

export interface GamePageAppearance {
  page_id: string;
  page_label: string;
  page_path: string;
  page_section: "games" | "topics";
  page_slug: string | null;
  appearance_source: GamePageAppearanceSource;
  row_count: number;
  row_names: string[];
}

// Temporarily augment the generated Games table type to include the
// new mobile_aspect_ratio column added via migration.
// When Supabase types are regenerated, this intersection will still be compatible.
// Note: seo_content is now included in the generated database types.
export type Game = Tables<"games"> & {
  mobile_aspect_ratio?:
    | "1:2"
    | "9:16"
    | "2:3"
    | "3:4"
    | "5:6"
    | "1:1"
    | "6:5"
    | "4:3"
    | "3:2"
    | "16:9"
    | "2:1"
    | null;
  mobile_orientation?: "portrait" | "landscape" | "both" | null;
  /** Admin override flag — when true, the catalog refresh preserves sort_order and size. */
  grid_override?: boolean | null;
  /** When true, excluded from /games grid ranking and public catalog grid display. */
  grid_hidden?: boolean | null;
  is_listed_on_published_page?: boolean;
  published_page_appearances_available?: boolean;
  published_page_appearances_partial?: boolean;
  published_page_appearances?: GamePageAppearance[];
};
export type Tag = Tables<"tags">;
export type Publisher = Tables<"publishers">;

// Game-specific types (for backward compatibility with existing queries)
export interface GameTag {
  id: number;
  name: string;
  slug: string;
  description: string | null;
  display_order: number | null;
  created_at: string;
  updated_at: string;
  // SEO fields are optional for game contexts
  published_as_webpage?: boolean;
  seo_title?: string | null;
  seo_subtitle?: string | null;
  seo_content?: string | null;
}

// Extended types with relationships
export interface GameWithRelations extends Game {
  tags?: GameTag[];
  publisher?: Publisher | null;
}

// For API responses
export interface GameRelationship {
  game_id: string;
  tag_id?: number;
}

// For form data
export interface GameFormData {
  name: string;
  slug: string;
  url?: string;
  thumbnail_url?: string;
  tutorial_video_url?: string;
  enable_tutorial_video?: boolean;
  desktop_only?: boolean;
  published?: boolean;
  archived?: boolean;
  premium_game?: boolean;
  new_tag?: boolean;
  publisher_id?: number;
  size?: number;
  sort_order?: number;
  tag_ids?: number[];
}

// Backwards compatibility interface for code that still uses numeric IDs
export interface GameLegacy extends Omit<Game, "id"> {
  id: number;
}
